import { useState } from 'react';
import { Phone, Mail, MapPin, Instagram, Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Reveal } from '@/components/Reveal';
import { CONTACT_INFO } from '@/lib/data';
import { createContact } from '@/lib/api';
import { LUXE } from '@/constants/testIds';

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    try {
      await createContact(form);
      setSent(true);
      toast.success('Message sent — we will be in touch shortly.');
      setForm({ name: '', email: '', message: '' });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-28">
      <section className="py-16 sm:py-24">
        <div className="luxe-container grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="luxe-eyebrow mb-4">Get in Touch</div>
              <h1 className="text-4xl sm:text-6xl text-white leading-[1.05]">
                Let's <span className="italic text-[#C9A24A]">talk</span> details.
              </h1>
              <div className="gold-divider mt-8" />
              <p className="mt-8 text-white/60 leading-relaxed">
                Questions about a package, a custom request, or just want to
                chat about your vehicle? We're here.
              </p>

              <div className="mt-12 space-y-6">
                <a
                  href={`tel:${CONTACT_INFO.phoneRaw}`}
                  data-testid={LUXE.contactPhoneLink}
                  className="flex items-start gap-4 p-5 border border-[#C9A24A]/20 hover:border-[#C9A24A]/60 transition-colors group"
                >
                  <Phone size={22} className="text-[#C9A24A] mt-1" />
                  <div>
                    <div className="luxe-eyebrow mb-1">Phone</div>
                    <div className="text-white text-lg group-hover:text-[#C9A24A] transition-colors">
                      {CONTACT_INFO.phone}
                    </div>
                  </div>
                </a>
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  data-testid={LUXE.contactEmailLink}
                  className="flex items-start gap-4 p-5 border border-[#C9A24A]/20 hover:border-[#C9A24A]/60 transition-colors group"
                >
                  <Mail size={22} className="text-[#C9A24A] mt-1 shrink-0" />
                  <div className="min-w-0">
                    <div className="luxe-eyebrow mb-1">Email</div>
                    <div className="text-white text-lg group-hover:text-[#C9A24A] transition-colors break-all">
                      {CONTACT_INFO.email}
                    </div>
                  </div>
                </a>
                <a
                  href={CONTACT_INFO.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="flex items-start gap-4 p-5 border border-[#C9A24A]/20 hover:border-[#C9A24A]/60 transition-colors group"
                >
                  <Instagram size={22} className="text-[#C9A24A] mt-1 shrink-0" />
                  <div>
                    <div className="luxe-eyebrow mb-1">Instagram</div>
                    <div className="text-white text-lg group-hover:text-[#C9A24A] transition-colors">
                      @{CONTACT_INFO.instagram}
                    </div>
                  </div>
                </a>
                <div className="flex items-start gap-4 p-5 border border-[#C9A24A]/20">
                  <MapPin size={22} className="text-[#C9A24A] mt-1 shrink-0" />
                  <div>
                    <div className="luxe-eyebrow mb-1">Service Area</div>
                    <div className="text-white text-lg">{CONTACT_INFO.area}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <div
                data-testid={LUXE.contactSuccess}
                className="luxe-card p-12 text-center border-[#C9A24A]/60"
              >
                <CheckCircle2 size={56} className="text-[#C9A24A] mx-auto mb-6" />
                <h2 className="text-3xl text-white mb-4">Message Sent</h2>
                <div className="gold-divider mx-auto mb-6" />
                <p className="text-white/70 max-w-md mx-auto">
                  Thanks for reaching out — we'll get back to you shortly.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  data-testid="contact-send-another"
                  className="btn-outline-gold mt-8"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form
                data-testid={LUXE.contactForm}
                onSubmit={handleSubmit}
                className="luxe-card p-8 sm:p-10 space-y-6"
              >
                <h2 className="text-2xl text-white mb-2">Send us a message</h2>
                <div className="gold-divider" />
                <div>
                  <label className="luxe-eyebrow block mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    data-testid={LUXE.contactName}
                    onChange={(e) => setField('name', e.target.value)}
                    placeholder="Your name"
                    className="luxe-input"
                  />
                </div>
                <div>
                  <label className="luxe-eyebrow block mb-2">Email</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    data-testid={LUXE.contactEmail}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="you@example.com"
                    className="luxe-input"
                  />
                </div>
                <div>
                  <label className="luxe-eyebrow block mb-2">Message</label>
                  <textarea
                    rows={6}
                    required
                    value={form.message}
                    data-testid={LUXE.contactMessage}
                    onChange={(e) => setField('message', e.target.value)}
                    placeholder="How can we help?"
                    className="luxe-input resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid={LUXE.contactSubmit}
                  className="btn-gold w-full disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      Send Message <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
